import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Briefcase, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';

interface FunderProject {
  id: string;
  name: string;
  description: string | null;
  status: string;
  domain: string;
  owner_name: string;
  owner_organization: string;
  funding_requested: number;
  funding_amount: number;
  created_at: string;
  has_analysis: boolean;
  analysis_grade?: string;
  analysis_score?: number;
}

export const FunderProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [projects, setProjects] = useState<FunderProject[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadProjects();
  }, [user]);

  const loadProjects = async () => {
    if (!user) return;

    try {
      setIsLoading(true);

      const { data: projectsData, error } = await supabase
        .from('projects')
        .select(`
          id,
          name,
          description,
          status,
          domain,
          funding_requested,
          funding_amount,
          created_at,
          owner:users!projects_owner_id_fkey(full_name, organization)
        `)
        .eq('assigned_funder_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const projectIds = projectsData?.map(p => p.id) || [];

      const { data: analysesData } = await supabase
        .from('financing_analyses')
        .select('project_id, bankability_grade, overall_score')
        .in('project_id', projectIds);

      const analysesMap = new Map(
        analysesData?.map(a => [a.project_id, { grade: a.bankability_grade, score: a.overall_score }])
      );

      const enrichedProjects = (projectsData || []).map(p => ({
        id: p.id,
        name: p.name,
        description: p.description,
        status: p.status,
        domain: p.domain,
        owner_name: (p.owner as any)?.full_name || 'Non renseigné',
        owner_organization: (p.owner as any)?.organization || '',
        funding_requested: p.funding_requested || 0,
        funding_amount: p.funding_amount || 0,
        created_at: p.created_at,
        has_analysis: analysesMap.has(p.id),
        analysis_grade: analysesMap.get(p.id)?.grade,
        analysis_score: analysesMap.get(p.id)?.score
      }));

      setProjects(enrichedProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      A_PREPARER: 'bg-gray-100 text-gray-800',
      SOUMIS: 'bg-blue-100 text-blue-800',
      ACCORD_PROVISOIRE: 'bg-yellow-100 text-yellow-800',
      VALIDE: 'bg-green-100 text-green-800',
      DEBLOCAGE_FONDS: 'bg-purple-100 text-purple-800',
      EN_COURS: 'bg-cyan-100 text-cyan-800',
      VERIFICATION: 'bg-orange-100 text-orange-800',
      TERMINE: 'bg-emerald-100 text-emerald-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      A_PREPARER: 'À préparer',
      SOUMIS: 'Soumis',
      ACCORD_PROVISOIRE: 'Accord provisoire',
      VALIDE: 'Validé',
      DEBLOCAGE_FONDS: 'Déblocage fonds',
      EN_COURS: 'En cours',
      VERIFICATION: 'Vérification',
      TERMINE: 'Terminé'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getGradeColor = (grade: string) => {
    const colors = {
      A: 'bg-green-100 text-green-800 border-green-300',
      B: 'bg-blue-100 text-blue-800 border-blue-300',
      C: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      D: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[grade as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" style={{ background: '#F7F8FC' }}>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mes projets assignés</h1>
          <p className="text-gray-600 mt-1">
            Projets ESS dont vous êtes le financeur référent
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Briefcase className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{projects.length}</p>
                <p className="text-sm text-gray-600">Projets assignés</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {projects.filter(p => p.has_analysis).length}
                </p>
                <p className="text-sm text-gray-600">Avec analyse</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {projects.filter(p => !p.has_analysis).length}
                </p>
                <p className="text-sm text-gray-600">En attente</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {projects.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aucun projet ne vous est assigné pour le moment.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/funder/projects/${project.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                          {getStatusLabel(project.status)}
                        </span>
                        {project.has_analysis && project.analysis_grade && (
                          <span className={`px-3 py-1 rounded-lg text-sm font-bold border-2 ${getGradeColor(project.analysis_grade)}`}>
                            Note {project.analysis_grade}
                          </span>
                        )}
                      </div>

                      <div className="space-y-1 mb-3">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Porteur :</span> {project.owner_name}
                          {project.owner_organization && ` - ${project.owner_organization}`}
                        </p>
                        {project.description && (
                          <p className="text-sm text-gray-500 line-clamp-2">{project.description}</p>
                        )}
                      </div>

                      <div className="flex items-center space-x-6 text-sm">
                        <div className="flex items-center space-x-2">
                          <TrendingUp className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">
                            Demandé : <span className="font-medium text-gray-900">
                              {project.funding_requested.toLocaleString('fr-FR')} €
                            </span>
                          </span>
                        </div>
                        {project.has_analysis && project.analysis_score && (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-gray-600">
                              Score : <span className="font-medium text-gray-900">
                                {project.analysis_score}/100
                              </span>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      className="flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                      style={{ background: '#2F47FF', color: 'white' }}
                      onMouseOver={(e) => e.currentTarget.style.background = '#6B4EFF'}
                      onMouseOut={(e) => e.currentTarget.style.background = '#2F47FF'}
                    >
                      <Eye className="w-4 h-4" />
                      <span>Voir l'analyse</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
